import React, { FC } from 'react';
import styled from 'styled-components';

import BackButton from 'src/presentation/components/layouts/table/BackButton';
import StackContainer from '@ad-scripts/presentation/components/common/containers/stack_container';
import { H1 } from '@ad-scripts/presentation/components/common/headers';
import Footer from 'src/presentation/components/forms/Footer';
import useResetErrorsOnUnmount from 'src/presentation/hooks/useResetErrorsOnUnmount';
import { Spinner } from './Spinner';

type FormPageProps = {
  backButtonUrl?: string;
  backButtonLabel?: string;
  'data-aid': string;
  errors?: any;
  form: any;
  item?: any;
  description?: any;
  isLoading: boolean;
  resetErrors?: any;
  title?: string;
  fillHeight?: boolean;
};

const FormPage: FC<FormPageProps> = ({
  backButtonUrl,
  backButtonLabel,
  'data-aid': dataAid,
  errors,
  form,
  item,
  isLoading,
  resetErrors,
  title,
  description,
  fillHeight,
}) => {
  const spinner = <Spinner />;
  return (
    <TableLayoutHeightRestrictor fillHeight={fillHeight}>
      <Flexer fillHeight={fillHeight}>
        <PageContainer data-aid={dataAid}>
          <HeaderContainer>
            {backButtonUrl && <BackButton label={backButtonLabel || 'Back'} backUrl={backButtonUrl} />}
            <H1 data-aid="form-page-header">{title}</H1>
            {description && <Description>{description}</Description>}
          </HeaderContainer>
          {isLoading ? spinner : null}
          {isLoading ? null : <Editor resetErrors={resetErrors} editor={form} item={item} errors={errors} />}
        </PageContainer>
      </Flexer>
    </TableLayoutHeightRestrictor>
  );
};

type EditorProps = { resetErrors: any; editor: any; item: any; errors: any };

const Editor: FC<EditorProps> = ({ resetErrors, editor, item, errors }) => {
  const EditorForm = editor;
  useResetErrorsOnUnmount(resetErrors);

  return <EditorForm item={item} errors={errors} />;
};

export const TableLayoutHeightRestrictor = styled.div`
  flex-grow: 1;
  overflow: auto;
  ${p =>
    !p.fillHeight &&
    `
      height: 100%;
    `}
`;

export const HeaderContainer = styled(StackContainer).attrs({ inset: 'large', stack: 'large' })`
  padding-bottom: 0;
`;

export const Description = styled(StackContainer).attrs({ stack: 'none', inset: 'none' })`
  color: ${p => p.theme.colors.gray700};
  line-height: 1.6;
`;

export const Flexer = styled.div`
  display: flex;
  ${p =>
    p.fillHeight &&
    `
      height: 100%;
    `}
`;

export const PageContainer = styled(StackContainer).attrs({ stack: 'none', inset: 'none' })`
  background-color: ${p => p.theme.colors.white};
  border-radius: ${p => p.theme.borderRadius.large};
  box-shadow: ${p => p.theme.boxShadow.default};
  flex-shrink: 1;
  margin: 24px 8px 24px 4px;
  max-width: 1200px;
  min-height: 0;
  overflow: auto;
  width: 100%;

  ${Footer} {
    box-shadow: ${p => p.theme.boxShadow.medium};
    border-top: 1px solid ${p => p.theme.colors.gray100};
  }
`;

export default FormPage;
